import { useState, useCallback } from 'react'
import FlashcardList from './FlashcardList.jsx'
import EmptyState from './EmptyState.jsx'
import FlashcardSkeleton from './FlashcardSkeleton.jsx'
import ErrorAlert from './ErrorAlert.jsx'
import DeleteAllButton from './DeleteAllButton.jsx'

/**
 * FlashcardSection component manages the flashcards display area.
 * Handles loading states, empty state, errors, and the flashcard list.
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.cards - Array of flashcard objects
 * @param {boolean} props.isInitialLoading - Initial data load state
 * @param {boolean} props.isRefetching - Refetch in progress
 * @param {string|null} props.dataError - Error message if any
 * @param {Set<string>} props.mutatingIds - Set of IDs being deleted or updated
 * @param {Function} props.onUpdate - Callback to update a flashcard
 * @param {Function} props.onDelete - Callback to delete a flashcard
 * @param {Function} [props.onRetry] - Optional retry callback for errors
 * @returns {React.ReactElement} Flashcard section
 */
export default function FlashcardSection({
  cards,
  isInitialLoading,
  isRefetching,
  dataError,
  mutatingIds,
  onUpdate,
  onDelete,
  onDeleteAll,
  isDeletingAll,
  onRetry,
}) {
  // Track which flashcard answers are revealed
  const [revealedIds, setRevealedIds] = useState(new Set())

  // Handle revealing a flashcard answer
  const handleReveal = useCallback((id) => {
    setRevealedIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])

  // Determine what to render based on state
  const hasCards = cards.length > 0
  const showSkeleton = isInitialLoading
  const showEmpty = !isInitialLoading && !dataError && !hasCards
  const showError = !isInitialLoading && dataError
  const showList = !isInitialLoading && !dataError && hasCards

  return (
    <section className="flashcard-section">
      <div className="flashcard-section__header">
        <h2 className="flashcard-section__title">Twoje fiszki</h2>
        {isRefetching && !isInitialLoading && (
          <span
            className="flashcard-section__refetch-indicator"
            aria-live="polite"
          >
            Odświeżanie...
          </span>
        )}
        {hasCards && (
          <DeleteAllButton onClick={onDeleteAll} isDeleting={isDeletingAll} />
        )}
      </div>

      <div className="flashcard-section__content">
        {/* Initial loading state */}
        {showSkeleton && <FlashcardSkeleton count={3} />}

        {/* Empty state */}
        {showEmpty && <EmptyState />}

        {/* Error state */}
        {showError && <ErrorAlert message={dataError} onRetry={onRetry} />}

        {/* Flashcard list */}
        {showList && (
          <FlashcardList
            cards={cards}
            revealedIds={revealedIds}
            onReveal={handleReveal}
            onUpdate={onUpdate}
            onDelete={onDelete}
            mutatingIds={mutatingIds}
          />
        )}
      </div>
    </section>
  )
}
